import React, {useState, useMemo} from 'react';
import {motion} from 'framer-motion';
import {useLanguage} from '../context/LanguageContext';
import SearchBar from '../components/SearchBar';
import CategoryFilter from '../components/CategoryFilter';
import ResourceCard from '../components/ResourceCard';
import HeroSection from '../components/HeroSection';
import {aiResourcesData} from '../data/aiResources';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiGrid, FiList, FiMaximize2, FiMinimize2} = FiIcons;

const Home = () => {
  const {t, language} = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('expanded'); // 'compact' or 'expanded'
  const [expandAll, setExpandAll] = useState(false);

  const categories = useMemo(() => {
    const cats = ['all', ...new Set(aiResourcesData.map(resource => resource.category))];
    return cats;
  }, []);

  const filteredResources = useMemo(() => {
    let filtered = aiResourcesData.filter(resource => {
      // Get description in current language for search
      const getDescription = () => {
        if (resource.description && typeof resource.description === 'object') {
          return resource.description[language] || resource.description['es'] || '';
        }
        return resource.description || '';
      };

      // Get tags in current language for search
      const getTags = () => {
        if (resource.tags && typeof resource.tags === 'object') {
          return resource.tags[language] || resource.tags['es'] || [];
        }
        return resource.tags || [];
      };

      const description = getDescription();
      const tags = getTags();

      const matchesSearch = searchTerm === '' ||
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [searchTerm, selectedCategory, language]);

  const handleTagClick = (tag) => {
    setSearchTerm(tag);
    setSelectedCategory('all'); // Reset category when clicking on tag
  };

  const handleCategoryReset = () => {
    setSelectedCategory('all');
  };

  // Memoize category translation to prevent infinite loops
  const getCategoryTranslation = useMemo(() => {
    return (category) => {
      const categoryMap = {
        'Investigación': t('investigacion'),
        'Texto y redacción': t('texto-redaccion'),
        'Educación': t('educacion'),
        'Chatbots': t('chatbots'),
        'Video': t('video'),
        'Mapas mentales': t('mapas-mentales'),
        'Imágenes': t('imagenes'),
        'Diseño': t('diseno'),
        'Generador de asistentes virtuales': t('generador-asistentes'),
        'Detector de contenidos de IA': t('detector-contenidos'),
        'Música': t('musica'),
        'Motores de búsqueda': t('motores-busqueda'),
        'Audio': t('audio'),
        'Redes sociales': t('redes-sociales')
      };
      return categoryMap[category] || category;
    };
  }, [t]);

  const handleExpandAllToggle = () => {
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);
    
    // When expandAll is true, force expanded view
    // When expandAll is false, maintain current viewMode
    if (newExpandAll) {
      setViewMode('expanded');
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    // Reset expandAll when changing view mode
    setExpandAll(false);
  };

  return (
    <div className="min-h-screen">
      <HeroSection />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter Controls */}
        <div className="mb-12">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onCategoryReset={handleCategoryReset}
          />
          
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        </div>

        {/* View Controls */}
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          className="mb-8 flex flex-col sm:flex-row justify-between items-center gap-4"
        >
          {/* Results Count */}
          <p className="text-gray-600 dark:text-gray-400">
            {t('mostrando')} {filteredResources.length}{' '}
            {filteredResources.length !== 1 ? t('recursos') : t('recurso')}
            {selectedCategory !== 'all' && ` ${t('en')} ${getCategoryTranslation(selectedCategory)}`}
          </p>

          {/* View Mode Controls */}
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => handleViewModeChange('compact')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'compact'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <SafeIcon icon={FiList} className="w-4 h-4" />
                <span className="hidden sm:inline">{t('vista-compacta')}</span>
              </button>
              
              <button
                onClick={() => handleViewModeChange('expanded')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'expanded'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <SafeIcon icon={FiGrid} className="w-4 h-4" />
                <span className="hidden sm:inline">{t('vista-expandida')}</span>
              </button>
            </div>

            <button
              onClick={handleExpandAllToggle}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={expandAll ? FiMinimize2 : FiMaximize2} className="w-4 h-4" />
              <span className="hidden sm:inline">
                {expandAll ? t('colapsar-todo') : t('expandir-todo')}
              </span>
            </button>
          </div>
        </motion.div>

        {/* Resources Grid */}
        {filteredResources.length > 0 ? (
          <motion.div
            layout
            className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {filteredResources.map((resource, index) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                index={index}
                onTagClick={handleTagClick}
                viewMode={viewMode}
                forceExpanded={expandAll}
              />
            ))}
          </motion.div>
        ) : (
          /* No Results */
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">🤖</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('no-encontraron')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('intenta-otros')}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('mostrar-todos')}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Home;